import React, { useEffect, useRef, useState, useMemo } from "react";
import { BarChart3, X, Search, BookMarked, Save, Settings } from "lucide-react";
import { INDICATORS, CATEGORIES } from "@/lib/indicators";
import { SYSTEM_PRESETS, loadCustomPresets, saveCustomPresets } from "@/lib/indicatorPresets";
import ZoneStyleEditor from "@/components/ZoneStyleEditor";
import { DEFAULT_ZONE_STYLES } from "@/lib/zoneStyles";

/**
 * Dropdown menu — sélection d'indicateurs à afficher sur le chart.
 * Cases à cocher regroupées par catégorie (Tendance / Momentum / Volatilité / Volume / Sentiment).
 * Recherche instantanée en haut du menu.
 *
 * Props:
 *   active: Set<string>   — noms d'indicateurs actifs
 *   onToggle: (name)=>void
 *   onReset: ()=>void
 */
export default function IndicatorsMenu({ active, onToggle, onReset, onApplyPreset, zoneStyles = DEFAULT_ZONE_STYLES, onZoneStylesChange }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [showPresets, setShowPresets] = useState(true);
  const [customPresets, setCustomPresets] = useState(loadCustomPresets);
  const [editingKind, setEditingKind] = useState(null);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const grouped = useMemo(() => {
    const query = q.trim().toLowerCase();
    const g = {};
    CATEGORIES.forEach((c) => { g[c] = []; });
    for (const ind of INDICATORS) {
      if (query && !ind.name.toLowerCase().includes(query) && !ind.label.toLowerCase().includes(query)) continue;
      (g[ind.cat] || (g[ind.cat] = [])).push(ind);
    }
    return g;
  }, [q]);

  const activeCount = active.size;

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`chip text-xs flex items-center gap-1.5 ${activeCount > 1 ? "bg-emerald-500/15 border-emerald-500/40 text-emerald-300" : "hover:bg-white/5"}`}
        data-testid="chart-indicators-btn"
        title="Ajouter/retirer des indicateurs techniques"
      >
        <BarChart3 size={12} /> Indicateurs{activeCount > 0 ? ` (${activeCount})` : ""}
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 z-[60] panel p-3 w-80 max-h-[70vh] flex flex-col" data-testid="chart-indicators-menu">
          <div className="flex items-center justify-between mb-2 shrink-0">
            <div className="text-xs font-semibold uppercase tracking-widest text-white/60">Indicateurs techniques</div>
            <button onClick={() => setOpen(false)} className="text-white/50 hover:text-white" aria-label="Fermer"><X size={13} /></button>
          </div>
          <div className="relative mb-2 shrink-0">
            <Search size={11} className="absolute left-2 top-1/2 -translate-y-1/2 text-white/40" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Rechercher (ex: RSI, MACD…)"
              className="w-full field-sm pl-6 text-[11px]"
              data-testid="chart-indicators-search"
            />
          </div>

          {/* Presets section */}
          <div className="shrink-0 mb-2">
            <button
              onClick={() => setShowPresets((v) => !v)}
              className="w-full flex items-center justify-between text-[10px] font-semibold uppercase tracking-widest text-white/50 hover:text-white mb-1.5"
              data-testid="chart-presets-toggle"
            >
              <span className="flex items-center gap-1"><BookMarked size={11} /> Presets</span>
              <span>{showPresets ? "▾" : "▸"}</span>
            </button>
            {showPresets && (
              <div className="space-y-1">
                <div className="grid grid-cols-2 gap-1">
                  {SYSTEM_PRESETS.map((p) => (
                    <button
                      key={p.id}
                      onClick={() => { onApplyPreset?.(p.indicators); }}
                      className="flex items-center gap-1.5 px-2 py-1 rounded-md border border-white/10 hover:border-accent-blue hover:bg-white/5 text-left"
                      data-testid={`preset-${p.id}`}
                      title={p.desc}
                    >
                      <span className="text-sm">{p.icon}</span>
                      <span className="text-[10px] font-medium truncate">{p.name}</span>
                    </button>
                  ))}
                </div>
                {customPresets.length > 0 && (
                  <div className="pt-1.5 border-t border-white/5 space-y-1">
                    <div className="text-[9px] uppercase text-white/40 font-semibold">Mes presets</div>
                    {customPresets.map((p, idx) => (
                      <div key={p.id} className="flex items-center gap-1">
                        <button
                          onClick={() => onApplyPreset?.(p.indicators)}
                          className="flex-1 flex items-center gap-1.5 px-2 py-1 rounded-md border border-white/10 hover:border-accent-blue hover:bg-white/5 text-left"
                          data-testid={`custom-preset-${p.id}`}
                        >
                          <span className="text-sm">💾</span>
                          <span className="text-[10px] font-medium truncate">{p.name} <span className="text-white/40">({p.indicators.length})</span></span>
                        </button>
                        <button
                          onClick={() => {
                            const next = customPresets.filter((_, i) => i !== idx);
                            setCustomPresets(next); saveCustomPresets(next);
                          }}
                          className="text-white/40 hover:text-red-400 px-1"
                          aria-label="Supprimer"
                          data-testid={`custom-preset-del-${p.id}`}
                        >
                          <X size={11} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                <button
                  onClick={() => {
                    const name = window.prompt("Nom du preset (ex: Mon setup) :");
                    if (!name || !name.trim()) return;
                    const next = [...customPresets, { id: `c-${Date.now()}`, name: name.trim(), indicators: Array.from(active) }];
                    setCustomPresets(next); saveCustomPresets(next);
                  }}
                  className="w-full flex items-center justify-center gap-1 text-[10px] py-1 border border-dashed border-white/15 hover:border-emerald-500/50 hover:bg-emerald-500/5 rounded-md text-white/60 hover:text-emerald-300"
                  data-testid="chart-presets-save"
                >
                  <Save size={10} /> Sauvegarder config actuelle ({active.size})
                </button>
              </div>
            )}
          </div>
          <div className="overflow-y-auto flex-1 space-y-2 pr-1 -mr-1">
            {CATEGORIES.map((cat) => (
              grouped[cat]?.length ? (
                <div key={cat}>
                  <div className="text-[9px] font-semibold uppercase tracking-widest text-white/40 mb-1">{cat}</div>
                  <div className="space-y-0.5">
                    {grouped[cat].map((ind) => {
                      const isActive = active.has(ind.name);
                      const hasZoneStyle = ind.custom && zoneStyles[ind.name];
                      return (
                        <div key={ind.name} className="relative">
                          <label
                            className={`flex items-center gap-2 px-2 py-1 rounded-md cursor-pointer transition-colors ${isActive ? "bg-emerald-500/10" : "hover:bg-white/5"}`}
                            data-testid={`indicator-item-${ind.name}`}
                          >
                            <input
                              type="checkbox"
                              checked={isActive}
                              onChange={() => onToggle(ind.name, ind.overlay)}
                              className="w-3 h-3 accent-emerald-500"
                              data-testid={`indicator-check-${ind.name}`}
                            />
                            <span className="text-[11px] flex-1">{ind.label}</span>
                            {hasZoneStyle && (
                              <button
                                type="button"
                                onClick={(e) => { e.preventDefault(); e.stopPropagation(); setEditingKind((k) => (k === ind.name ? null : ind.name)); }}
                                className={`p-0.5 rounded hover:bg-white/10 ${editingKind === ind.name ? "text-accent-blue" : "text-white/40 hover:text-white"}`}
                                title="Propriétés (remplissage, contour, opacité, couleurs, zones non-mitigées)"
                                data-testid={`indicator-gear-${ind.name}`}
                              >
                                <Settings size={11} />
                              </button>
                            )}
                            <span className={`text-[9px] font-mono px-1 rounded ${ind.overlay ? "bg-blue-500/20 text-blue-300" : "bg-white/5 text-white/40"}`}>
                              {ind.overlay ? "overlay" : "pane"}
                            </span>
                          </label>
                          {editingKind === ind.name && onZoneStylesChange && (
                            <ZoneStyleEditor
                              kind={ind.name}
                              zoneStyles={zoneStyles}
                              onChange={onZoneStylesChange}
                              onClose={() => setEditingKind(null)}
                            />
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : null
            ))}
            {Object.values(grouped).every((arr) => arr.length === 0) && (
              <div className="text-[11px] text-white/40 text-center py-4">Aucun résultat</div>
            )}
          </div>
          {activeCount > 1 && (
            <button
              onClick={onReset}
              className="mt-2 w-full text-[10px] py-1.5 rounded-md border border-white/10 hover:border-white/30 shrink-0"
              data-testid="chart-indicators-reset"
            >
              Tout désactiver (garder Volume)
            </button>
          )}
        </div>
      )}
    </div>
  );
}
